import * as yup from 'yup'
import useYupValidationResolver from '../hooks/YupValidationResolver'
import { useForm } from 'react-hook-form'
import InputField from '../components/form-elements/InputField'
import { LoginChallenge, LoginResponse, useAuth } from '../hooks/AuthProvider'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DocumentTitle from 'react-document-title'
import Swal from 'sweetalert2'


export default function Login() {

    const [error, setError] = useState<string | undefined>(undefined)
    const [loginChallenge, setLoginChallenge] = useState<LoginChallenge | null>(null)
    const auth = useAuth();

    const isLoginChallenge = (response: LoginResponse): response is LoginChallenge => {
        return 'login_token' in response;
    };

    const navigate = useNavigate();
    useEffect(() => {
        if (auth.user?.id || auth.user?.email) {
            navigate('/home');
        }
    }, [auth.user, navigate])

    const credentialSchema = {
        username: yup.string().required('Username is required'),
        password: yup
            .string()
            .required('Password is required'),
    }

    const loginSchema = yup.object({
        username: credentialSchema.username,
        password: credentialSchema.password,
    })

    const resolver = useYupValidationResolver(loginSchema)

    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm({ resolver })

    const verifySchema = yup.object({
        code: yup.string().required('OTP code is required'),
    })

    const verifyResolver = useYupValidationResolver(verifySchema)

    const {
        register: registerOtp,
        handleSubmit: handleOtpSubmit,
        formState: { errors: otpErrors },
        reset: resetOtpForm,
    } = useForm({ resolver: verifyResolver })

    const onSubmit = async (data: any) => {
        try {
            Swal.showLoading();
            setError(undefined)
            const response = await auth.login(data.username, data.password);

            if (isLoginChallenge(response)) {
                resetOtpForm({ code: '' });
                setLoginChallenge(response);
            } else {
                setLoginChallenge(null);
            }

            Swal.close();
        } catch (error: any) {
            Swal.close();
            setError(error.message);
        }
    }

    const onVerifySubmit = async (data: any) => {
        if (!loginChallenge?.login_token) {
            return;
        }

        try {
            Swal.showLoading();
            setError(undefined)
            await auth.verifyLoginOtp(loginChallenge.login_token, data.code);
            Swal.close();
        } catch (error: any) {
            Swal.close();
            setError(error.message);
        }
    }

    const handleResendCode = async () => {
        if (!loginChallenge?.login_token) {
            return;
        }

        try {
            Swal.showLoading();
            setError(undefined)
            const challenge = await auth.resendLoginOtp(loginChallenge.login_token);
            setLoginChallenge(challenge);
            Swal.close();
            Swal.fire({
                title: t('Success'),
                text: t('A new verification code has been sent to your email.'),
                icon: 'success'
            });
        } catch (error: any) {
            Swal.close();
            setError(error.message);
        }
    }

    const resetLoginChallenge = () => {
        setError(undefined);
        setLoginChallenge(null);
        resetOtpForm();
    }

    const [t] = useTranslation('common');
    return <div className="flex  flex-col justify-center items-center px-4 lg:px-8 h-dvh  overflow-hidden">
        <DocumentTitle title={t('Login')}></DocumentTitle>
        <div className="login grid h-dvh w-full items-center">
            <div className="logo-header text-center w-full">
                <div className="logo max-w-[210px] mx-auto">
                    <img className="max-w-full h-auto" src="./assets/img/logo.png" alt='Savills Logo' />
                </div>
                <div className="text text-center ">
                    <h2 className="mt-8 text-2xl font-medium capitalize text-sablack">
                        {loginChallenge ? t('Verify your login') : <>Sign in to your <br />account</>}
                    </h2>
                    {loginChallenge ? <p className="mt-3 text-xs text-slate-600 max-w-[450px] mx-auto">
                        {t('We sent a verification code to your email. Enter it below to finish signing in.')}
                    </p> : <></>}
                </div>
                <div className="form mt-4 mx-auto">
                    <form onSubmit={loginChallenge ? handleOtpSubmit(onVerifySubmit) : handleSubmit(onSubmit)} className="login-form space-y-6  max-w-[450px] mx-auto">
                        {error ? <div className='flex items-start bg-salightred  text-sared text-xs text-start border rounded-md border-sared px-3 py-2 '>
                            {error}
                        </div> : <></>}
                        {loginChallenge ? <>
                            <div className='flex items-start bg-slate-50 text-slate-700 text-xs text-start border rounded-md border-slate-200 px-3 py-2 '>
                                {t('Signing in as')} {getValues('username')}
                            </div>
                            <InputField
                                key="otp-code"
                                label={t("OTP code")}
                                type="text"
                                placeholder="123456"
                                autoComplete={'one-time-code'}
                                error={otpErrors.code}
                                {...registerOtp('code')}
                            />
                            <button
                                type="button"
                                onClick={handleResendCode}
                                className='w-full text-right block text-sared text-xs font-medium underline-offset-1 cursor-pointer mx-auto'
                            >
                                {t("Resend verification code")}
                            </button>
                            <button
                                type="button"
                                onClick={resetLoginChallenge}
                                className='w-full text-right block text-slate-500 text-xs font-medium underline-offset-1 cursor-pointer mx-auto'
                            >
                                {t("Use a different account")}
                            </button>
                        </> : <>
                            <InputField
                                key="login-username"
                                label={t("Username")}
                                type="username"
                                placeholder="Username"
                                autoComplete={'username'}
                                error={errors.username}
                                {...register('username')}
                            />

                            <InputField
                                key="login-password"
                                label={t("Password")}
                                type="password"
                                placeholder="*************"
                                autoComplete={'current-password'}
                                error={errors.password}
                                {...register('password')}
                            />
                            <Link to="/forgot-password" className='w-full text-right  block text-sared text-xs font-medium underline-offset-1 cursor-pointer mx-auto'>
                                {t("Forgot Password")}
                            </Link>
                        </>}
                        <input
                            type="submit"
                            value={loginChallenge ? t("Verify Code") : t("Login")}
                            className="hover:cursor-pointer flex w-full justify-center rounded-lg px-3  h-[50px] text-sm font-semibold leading-6 text-white shadow-sm bg-sared hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        />
                    </form>

                </div>
            </div>
        </div>
    </div>
}
